import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { BlockType } from "@worktools/shared";
import { useUi } from "../state/ui";
import { clampMenu, submenuSide } from "../ui/menuPosition";
import { useBoard } from "../sync/BoardContext";
import { useLocalAwareness } from "../sync/hooks";
import {
  addBlockAt,
  bringToFront,
  copyBlocks,
  deleteBlocks,
  duplicateBlocks,
  hasClipboard,
  pasteBlocks,
  sendToBack,
  setAccent,
  toggleLock,
} from "../board/actions";
import { useAwarenessStates, useBlockFrames } from "../sync/hooks";
import { useDialogs } from "../ui/dialogs";

const BLOCK_LABELS: { type: BlockType; label: string }[] = [
  { type: "checklist", label: "Checkliste" },
  { type: "timer", label: "Timer" },
  { type: "note", label: "Notizzettel" },
  { type: "text", label: "Text" },
  { type: "card", label: "Karte" },
  { type: "table", label: "Tabelle" },
  { type: "diagram", label: "Diagramm" },
];

const ACCENTS = ["#1f7a72", "#b4532a", "#3a5ba0", "#a63d5f", "#8a7320"];

const SUBMENU_OFFSET = 4;

export function ContextMenu({
  viewportRef,
  zoomToFit,
}: {
  viewportRef: React.RefObject<HTMLDivElement | null>;
  zoomToFit: (ids?: string[]) => void;
}) {
  const { doc, me } = useBoard();
  const menu = useUi((state) => state.contextMenu);
  const close = useUi((state) => state.closeContextMenu);
  const selection = useUi((state) => state.selection);
  const setSelection = useUi((state) => state.setSelection);
  const snapToGrid = useUi((state) => state.snapToGrid);
  const setSnapToGrid = useUi((state) => state.setSnapToGrid);
  const camera = useUi((state) => state.camera);
  const frames = useBlockFrames();
  const remotes = useAwarenessStates();
  const publish = useLocalAwareness();
  const dialogs = useDialogs();
  const ref = useRef<HTMLDivElement>(null);
  const [submenu, setSubmenu] = useState<"add" | "accent" | null>(null);
  const subRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [side, setSide] = useState<"left" | "right">("right");
  const [subTop, setSubTop] = useState(4);

  // Measure after mount so a menu opened near an edge stays fully reachable.
  useLayoutEffect(() => {
    if (!menu || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const clamped = clampMenu(menu.screen, { width: rect.width, height: rect.height }, viewport);
    setPosition(clamped);
    setSide(submenuSide(clamped.x, rect.width, 210, viewport));

    const sub = subRef.current;
    if (sub) {
      const subRect = sub.getBoundingClientRect();
      const desired = clamped.y + SUBMENU_OFFSET;
      const maxTop = viewport.height - subRect.height - 8;
      setSubTop(Math.max(8, Math.min(desired, maxTop)) - clamped.y);
    }
  }, [menu, submenu]);

  useEffect(() => {
    setSubmenu(null);
  }, [menu]);

  useEffect(() => {
    if (!menu) return;
    const dismiss = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) close();
    };
    window.addEventListener("mousedown", dismiss);
    return () => window.removeEventListener("mousedown", dismiss);
  }, [menu, close]);

  if (!menu) return null;

  const ids = menu.target.kind === "block" ? (selection.length ? selection : [menu.target.blockId]) : selection;
  const run = (action: () => void) => () => {
    action();
    close();
  };

  const spotlightOn = (blockIds: string[]) => {
    const bounds = frames.filter((frame) => blockIds.includes(frame.id));
    const rect = viewportRef.current?.getBoundingClientRect();
    const target = bounds[0];
    const nextCamera = target
      ? {
          zoom: camera.zoom,
          x: -target.x + ((rect?.width ?? 1200) / camera.zoom - target.w) / 2,
          y: -target.y + ((rect?.height ?? 800) / camera.zoom - target.h) / 2,
        }
      : camera;
    publish({ spotlight: { at: Date.now(), camera: nextCamera, blockIds } });
    notifySpotlight();
  };

  const notifySpotlight = () => {
    dialogs.toast(
      remotes.length === 0
        ? "Niemand sonst ist gerade im Board"
        : `${remotes.length} ${remotes.length === 1 ? "Teilnehmer wurde" : "Teilnehmer wurden"} gerufen`,
    );
  };

  return (
    <div
      className="menu"
      ref={ref}
      style={{ left: position?.x ?? menu.screen.x, top: position?.y ?? menu.screen.y }}
    >
      {menu.target.kind === "canvas" ? (
        <>
          <button className="menu__item" onMouseEnter={() => setSubmenu("add")} onClick={() => setSubmenu("add")}>
            Block hinzufügen <span className="shortcut">▸</span>
          </button>
          {submenu === "add" && (
            <div className="menu menu--sub" ref={subRef} style={submenuStyle(side, subTop)}>
              {BLOCK_LABELS.map((item) => (
                <button
                  key={item.type}
                  className="menu__item"
                  onClick={run(() => {
                    const id = addBlockAt(doc, item.type, menu.target.canvasPoint, me.id);
                    setSelection([id]);
                  })}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
          <button
            className="menu__item"
            disabled={!hasClipboard()}
            onClick={run(() => setSelection(pasteBlocks(doc, menu.target.canvasPoint, me.id)))}
          >
            Einfügen <span className="shortcut">⌘V</span>
          </button>
          <div className="menu__sep" />
          <button className="menu__item" onClick={run(() => setSelection(frames.map((frame) => frame.id)))}>
            Alles auswählen <span className="shortcut">⌘A</span>
          </button>
          <button className="menu__item" onClick={run(() => zoomToFit())}>
            Auf Inhalt zoomen <span className="shortcut">⌘1</span>
          </button>
          <button className="menu__item" onClick={run(() => setSnapToGrid(!snapToGrid))}>
            Am Raster ausrichten <span className="shortcut">{snapToGrid ? "an" : "aus"}</span>
          </button>
          <div className="menu__sep" />
          <button
            className="menu__item"
            onClick={run(() => {
              publish({ spotlight: { at: Date.now(), camera, blockIds: [] } });
              notifySpotlight();
            })}
          >
            Alle hierher holen
          </button>
        </>
      ) : (
        <>
          <button className="menu__item" onClick={run(() => setSelection(duplicateBlocks(doc, ids, me.id)))}>
            Duplizieren <span className="shortcut">⌘D</span>
          </button>
          <button className="menu__item" onClick={run(() => copyBlocks(doc, ids))}>
            Kopieren <span className="shortcut">⌘C</span>
          </button>
          <div className="menu__sep" />
          <button className="menu__item" onClick={run(() => bringToFront(doc, ids))}>
            Nach vorn
          </button>
          <button className="menu__item" onClick={run(() => sendToBack(doc, ids))}>
            Nach hinten
          </button>
          <button className="menu__item" onClick={run(() => toggleLock(doc, ids))}>
            Sperren umschalten
          </button>
          <button className="menu__item" onMouseEnter={() => setSubmenu("accent")} onClick={() => setSubmenu("accent")}>
            Akzentfarbe <span className="shortcut">▸</span>
          </button>
          {submenu === "accent" && (
            <div
              className="menu menu--sub"
              ref={subRef}
              style={{ ...submenuStyle(side, subTop), minWidth: 0, display: "flex", gap: 6 }}
            >
              {ACCENTS.map((color) => (
                <button
                  key={color}
                  className="swatch"
                  style={{ background: color, width: 18, height: 18 }}
                  onClick={run(() => setAccent(doc, ids, color))}
                />
              ))}
              <button className="swatch" style={{ width: 18, height: 18 }} onClick={run(() => setAccent(doc, ids, undefined))} />
            </div>
          )}
          <div className="menu__sep" />
          <button className="menu__item" onClick={run(() => zoomToFit(ids))}>
            Darauf zoomen
          </button>
          <button className="menu__item" onClick={run(() => spotlightOn(ids))}>
            Alle hierauf lenken
          </button>
          <div className="menu__sep" />
          <button
            className="menu__item menu__item--danger"
            onClick={run(() => {
              deleteBlocks(doc, ids);
              setSelection([]);
            })}
          >
            Löschen <span className="shortcut">⌫</span>
          </button>
        </>
      )}
    </div>
  );
}

function submenuStyle(side: "left" | "right", top: number): React.CSSProperties {
  return side === "right"
    ? { left: "100%", top, marginLeft: 4 }
    : { right: "100%", top, marginRight: 4 };
}
